import { useState } from 'react';
import { getSkillModifier, getSavingThrowModifier } from '@character-creator/core';
import type { AbilityName, SkillName } from '@character-creator/types';
import { useDiceRollerStore } from '../../stores/diceRollerStore';
import type { PlayModeSheetProps } from './sheet/types';
import { AbilityScoresColumn } from './sheet/AbilityScoresColumn';
import { CombatStatsColumn } from './sheet/CombatStatsColumn';
import { ResourcesColumn } from './sheet/ResourcesColumn';
import { BelowFoldSections } from './sheet/BelowFoldSections';

export function PlayModeSheet({
  benefits,
  session,
  equipment,
  draft,
  spells,
  onDamage: _onDamage,
  onHeal: _onHeal,
  onToggleCondition,
  onDeathSaveSuccess,
  onDeathSaveFailure,
  onUseHitDie,
  onUseSpellSlot,
  onRestoreSpellSlot,
  onUseResource,
  onRestoreResource,
  onSetConcentration,
  onToggleBardicInspiration,
  onToggleDmInspiration,
  onRemoveCustomEffect,
}: PlayModeSheetProps) {
  // These props are available for future use
  void _onDamage;
  void _onHeal;

  const { triggerRoll } = useDiceRollerStore();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Helper to build d20 notation with modifier
  const rollD20 = (label: string, modifier: number) => {
    const sign = modifier >= 0 ? '+' : '';
    triggerRoll(`1d20${sign}${modifier}`, label);
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  // Skill modifier via core
  const getSkillMod = (skill: SkillName) => {
    const { modifier, proficiencyLevel } = getSkillModifier(benefits, skill);
    return {
      mod: modifier,
      isProficient: proficiencyLevel !== 'none',
      hasExpertise: proficiencyLevel === 'expertise',
    };
  };

  // Save modifier via core
  const getSaveMod = (ability: AbilityName) => {
    const { modifier, isProficient } = getSavingThrowModifier(benefits, ability);
    return { mod: modifier, isProficient };
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[180px_1fr_260px] gap-4 max-w-7xl mx-auto">
      {/* Left Column - Ability Scores & Saving Throws */}
      <AbilityScoresColumn
        benefits={benefits}
        onRollAbility={rollD20}
        getSaveMod={getSaveMod}
      />

      {/* Center Column - Combat Stats & Skills */}
      <CombatStatsColumn
        benefits={benefits}
        session={session}
        onRollAbility={rollD20}
        getSkillMod={getSkillMod}
      />

      {/* Right Column - Resources & Features */}
      <ResourcesColumn
        benefits={benefits}
        session={session}
        equipment={equipment}
        onDeathSaveSuccess={onDeathSaveSuccess}
        onDeathSaveFailure={onDeathSaveFailure}
        onUseHitDie={onUseHitDie}
        onUseSpellSlot={onUseSpellSlot}
        onRestoreSpellSlot={onRestoreSpellSlot}
        onToggleCondition={onToggleCondition}
        onUseResource={onUseResource}
        onRestoreResource={onRestoreResource}
        onToggleBardicInspiration={onToggleBardicInspiration}
        onToggleDmInspiration={onToggleDmInspiration}
        onRemoveCustomEffect={onRemoveCustomEffect}
      />

      {/* Below the Fold - Comprehensive Sections */}
      <BelowFoldSections
        benefits={benefits}
        session={session}
        draft={draft}
        equipment={equipment}
        spells={spells}
        expandedSections={expandedSections}
        onToggleSection={toggleSection}
        onRollAttack={rollD20}
        onRollDamage={triggerRoll}
        onUseSpellSlot={onUseSpellSlot}
        onSetConcentration={onSetConcentration}
      />
    </div>
  );
}
